import { useMemo, useState } from 'react'
import { Box, Chip, LinearProgress, Stack, Typography } from '@mui/material'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { SearchField } from '@components/common/SearchField'
import { storageReportApi } from '@api/endpoints/storageReport'
import type { UntrackedObject, UntrackedPrefix } from '@api/types/storageReport'
import { STORAGE_REPORT_KEY, apiErrorMessage, formatBytes, formatDateTime } from './storageFormat'

export interface UntrackedObjectsTableProps {
  prefixes: UntrackedPrefix[]
  reportKey: string
}

/**
 * Bucket objects with no media record. Grouped by top-level prefix first, because the prefix is
 * usually what tells you whether an object is debris or something written on purpose without a record.
 */
export function UntrackedObjectsTable({ prefixes, reportKey }: UntrackedObjectsTableProps) {
  const [prefix, setPrefix] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(50)
  const [search, setSearch] = useState('')

  const query = useQuery({
    queryKey: [...STORAGE_REPORT_KEY, 'untracked', reportKey, prefix, search, page, pageSize],
    queryFn: () =>
      storageReportApi.untracked({ prefix: prefix ?? undefined, search: search || undefined, page: page + 1, pageSize }),
    placeholderData: keepPreviousData,
  })

  const columns = useMemo<DataTableColumn<UntrackedObject>[]>(
    () => [
      {
        key: 'key',
        header: 'Object',
        primary: true,
        render: (obj) => (
          <Typography variant="body2" sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>
            {obj.key}
          </Typography>
        ),
      },
      { key: 'size', header: 'Size', align: 'right', render: (obj) => formatBytes(obj.size) },
      { key: 'modified', header: 'Last modified', hideBelow: 'md', render: (obj) => formatDateTime(obj.lastModified) },
      {
        key: 'producer',
        header: 'Written by',
        hideBelow: 'lg',
        render: (obj) =>
          obj.knownProducer ? (
            <Typography variant="body2">{obj.knownProducer}</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Unknown
            </Typography>
          ),
      },
    ],
    [],
  )

  const choosePrefix = (value: string | null) => {
    setPrefix(value)
    setPage(0)
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Chip label="All prefixes" color={prefix === null ? 'primary' : 'default'} onClick={() => choosePrefix(null)} />
        {prefixes.map((p) => (
          <Chip
            key={p.prefix}
            color={prefix === p.prefix ? 'primary' : 'default'}
            variant={prefix === p.prefix ? 'filled' : 'outlined'}
            onClick={() => choosePrefix(p.prefix)}
            label={`${p.prefix} · ${p.count.toLocaleString()} · ${formatBytes(p.bytes)}`}
            title={p.knownProducer ?? 'No known producer'}
          />
        ))}
      </Stack>

      <SearchField
        value={search}
        onSearch={(q) => {
          setSearch(q)
          setPage(0)
        }}
        placeholder="Search object keys"
        maxWidth="100%"
        sx={{ mb: 1.5 }}
      />

      {/* Paging keeps the old rows up while the next page loads; this shows it is loading. */}
      <Box sx={{ height: 4, mb: 0.5 }}>{query.isFetching && !query.isLoading && <LinearProgress />}</Box>

      <DataTable<UntrackedObject>
        rows={query.data?.items ?? []}
        columns={columns}
        getRowKey={(obj) => obj.key}
        loading={query.isLoading}
        error={query.error ? apiErrorMessage(query.error, 'Failed to load bucket objects') : null}
        onRetry={() => query.refetch()}
        emptyMessage={search ? 'Nothing matches that search.' : 'Every object in the bucket has a media record.'}
        page={page}
        pageSize={pageSize}
        total={query.data?.totalCount ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(0)
        }}
        pagerPosition="both"
      />
    </Box>
  )
}
